import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

export const TOAST_PRIORITY = {
  LOW: 'LOW',
  NORMAL: 'NORMAL',
  HIGH: 'HIGH',
  CRITICAL: 'CRITICAL',
};

export const TOAST_TYPE = {
  TRADE_SUCCESS: 'TRADE_SUCCESS',
  TRADE_FAILED: 'TRADE_FAILED',
  CRIME_SUCCESS: 'CRIME_SUCCESS',
  CRIME_JAIL: 'CRIME_JAIL',
  FACTION_DEPOSIT: 'FACTION_DEPOSIT',
  FACTION_WAR: 'FACTION_WAR',
  PRICE_ALERT: 'PRICE_ALERT',
  SUCCESS: 'SUCCESS',
  ERROR: 'ERROR',
  INFO: 'INFO',
  WARNING: 'WARNING',
};

// Persistence levels
export const TOAST_PERSISTENCE = {
  TRANSIENT: 'transient',    // auto-dismiss, not persisted
  PERSISTENT: 'persistent',  // no auto-dismiss, persisted until manually dismissed
  CRITICAL: 'critical',      // no auto-dismiss, persisted, requires action
};

// Duration by type
const TYPE_DURATIONS = {
  SUCCESS: 5000,
  ERROR: 8000,
  INFO: 3000,
  WARNING: 10000,
  TRADE_SUCCESS: 5000,
  TRADE_FAILED: 8000,
  CRIME_SUCCESS: 5000,
  CRIME_JAIL: 10000,
  FACTION_DEPOSIT: 5000,
  FACTION_WAR: null,
  PRICE_ALERT: 8000,
};

const TYPE_CONFIG = {
  TRADE_SUCCESS: { icon: 'CheckCircle', colorClass: 'bg-emerald-900/95 border-emerald-500/50 text-emerald-200', iconColor: '#34D399', barColor: '#34D399' },
  TRADE_FAILED: { icon: 'XCircle', colorClass: 'bg-red-900/95 border-red-500/50 text-red-200', iconColor: '#F87171', barColor: '#F87171' },
  CRIME_SUCCESS: { icon: 'DollarSign', colorClass: 'bg-emerald-900/95 border-emerald-500/50 text-emerald-200', iconColor: '#34D399', barColor: '#34D399' },
  CRIME_JAIL: { icon: 'AlertTriangle', colorClass: 'bg-red-900/95 border-red-500/50 text-red-200', iconColor: '#F87171', barColor: '#F87171' },
  FACTION_DEPOSIT: { icon: 'Vault', colorClass: 'bg-blue-900/95 border-blue-500/50 text-blue-200', iconColor: '#60A5FA', barColor: '#60A5FA' },
  FACTION_WAR: { icon: 'Swords', colorClass: 'bg-orange-900/95 border-orange-500/60 text-orange-200', iconColor: '#FB923C', barColor: '#FB923C' },
  PRICE_ALERT: { icon: 'TrendingUp', colorClass: 'bg-yellow-900/95 border-yellow-500/50 text-yellow-200', iconColor: '#FBBF24', barColor: '#FBBF24' },
  SUCCESS: { icon: 'CheckCircle', colorClass: 'bg-emerald-900/95 border-emerald-500/50 text-emerald-200', iconColor: '#34D399', barColor: '#34D399' },
  ERROR: { icon: 'XCircle', colorClass: 'bg-red-900/95 border-red-500/50 text-red-200', iconColor: '#F87171', barColor: '#F87171' },
  INFO: { icon: 'Info', colorClass: 'bg-blue-900/95 border-blue-500/50 text-blue-200', iconColor: '#60A5FA', barColor: '#60A5FA' },
  WARNING: { icon: 'AlertTriangle', colorClass: 'bg-orange-900/95 border-orange-500/50 text-orange-200', iconColor: '#FB923C', barColor: '#FB923C' },
};

const QUEUE_STORAGE_KEY = 'empirex_toast_queue';
const MAX_VISIBLE = 5;

const ToastContext = createContext(null);

// Generate unique ID
const genId = () => `toast-${Date.now()}-${Math.random()?.toString(36)?.slice(2, 7)}`;

// Load persisted queue from localStorage
const loadPersistedQueue = () => {
  try {
    const stored = localStorage.getItem(QUEUE_STORAGE_KEY);
    if (stored) {
      const queue = JSON.parse(stored);
      if (Array.isArray(queue)) return queue;
    }
  } catch (e) {}
  return [];
};

// Save persistent/critical toasts to localStorage
const saveToQueue = (toasts) => {
  try {
    const toSave = toasts?.filter(t =>
      t?.persistence === TOAST_PERSISTENCE?.PERSISTENT ||
      t?.persistence === TOAST_PERSISTENCE?.CRITICAL
    );
    if (toSave?.length > 0) {
      localStorage.setItem(QUEUE_STORAGE_KEY, JSON.stringify(toSave?.slice(-20)));
    } else {
      localStorage.removeItem(QUEUE_STORAGE_KEY);
    }
  } catch (e) {}
};

const removeFromQueue = (id) => {
  try {
    const stored = localStorage.getItem(QUEUE_STORAGE_KEY);
    if (stored) {
      const queue = JSON.parse(stored);
      const updated = queue?.filter(t => t?.id !== id);
      if (updated?.length === 0) localStorage.removeItem(QUEUE_STORAGE_KEY);
      else localStorage.setItem(QUEUE_STORAGE_KEY, JSON.stringify(updated));
    }
  } catch (e) {}
};

export const ToastProvider = ({ children, position = 'bottom-right' }) => {
  const [toasts, setToasts] = useState([]);
  const [toastPosition, setToastPosition] = useState(position);
  const timersRef = useRef({});

  // Load persisted toasts on mount
  useEffect(() => {
    const persisted = loadPersistedQueue();
    if (persisted?.length > 0) {
      setToasts(persisted?.map(t => ({ ...t, restored: true })));
    }
  }, []);

  // Save to localStorage whenever toasts change
  useEffect(() => {
    saveToQueue(toasts);
  }, [toasts]);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev?.filter(t => t?.id !== id));
    if (timersRef?.current?.[id]) {
      clearTimeout(timersRef?.current?.[id]);
      delete timersRef?.current?.[id];
    }
    removeFromQueue(id);
  }, []);

  const showToast = useCallback((message, type = TOAST_TYPE?.INFO, options = {}) => {
    const typeConfig = TYPE_CONFIG?.[type] || TYPE_CONFIG?.[TOAST_TYPE?.INFO];
    const duration = options?.duration !== undefined
      ? options?.duration
      : (TYPE_DURATIONS?.[type] !== undefined ? TYPE_DURATIONS?.[type] : 5000);

    // Determine persistence level
    let persistence = options?.persistence || TOAST_PERSISTENCE?.TRANSIENT;
    if (type === TOAST_TYPE?.FACTION_WAR) persistence = TOAST_PERSISTENCE?.CRITICAL;
    if (options?.persistence) persistence = options?.persistence;

    // No auto-dismiss for persistent/critical
    const effectiveDuration = (persistence === TOAST_PERSISTENCE?.TRANSIENT) ? duration : null;

    const id = genId();
    const toast = {
      id,
      message,
      type,
      title: options?.title || null,
      colorClass: typeConfig?.colorClass,
      iconColor: typeConfig?.iconColor,
      barColor: typeConfig?.barColor,
      icon: typeConfig?.icon,
      duration: effectiveDuration,
      persistence,
      createdAt: Date.now(),
      actions: options?.actions || null, // [{ label, onClick, primary }]
    };

    // Haptic for critical
    if (persistence === TOAST_PERSISTENCE?.CRITICAL && navigator?.vibrate) {
      navigator.vibrate([200, 100, 200]);
    }

    setToasts(prev => {
      const updated = [...prev, toast];
      return updated?.slice(-MAX_VISIBLE);
    });

    if (effectiveDuration) {
      timersRef.current[id] = setTimeout(() => removeToast(id), effectiveDuration);
    }

    return id;
  }, [removeToast]);

  const dismissAll = useCallback(() => {
    Object.values(timersRef?.current)?.forEach(clearTimeout);
    timersRef.current = {};
    setToasts([]);
    localStorage.removeItem(QUEUE_STORAGE_KEY);
  }, []);

  return (
    <ToastContext.Provider value={{ showToast, dismissAll, toasts, toastPosition, setToastPosition }}>
      {children}
      <ToastStack
        toasts={toasts}
        onDismiss={removeToast}
        onDismissAll={dismissAll}
        position={toastPosition}
      />
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
};

// ── Progress Bar ──────────────────────────────────────────────
const ProgressBar = ({ duration, barColor, paused }) => {
  const [width, setWidth] = useState(100);
  const startRef = useRef(Date.now());
  const rafRef = useRef(null);

  useEffect(() => {
    if (!duration || paused) return;
    const tick = () => {
      const elapsed = Date.now() - startRef?.current;
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
      setWidth(remaining);
      if (remaining > 0) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef?.current);
  }, [duration, paused]);

  if (!duration) return null;

  return (
    <div className="absolute bottom-0 left-0 right-0 h-0.5 rounded-b-xl overflow-hidden bg-white/10">
      <div
        className="h-full transition-none"
        style={{ width: `${width}%`, background: barColor, transition: 'none' }}
      />
    </div>
  );
};

// ── Toast Icons ───────────────────────────────────────────────
const ToastIcon = ({ name, color }) => {
  const icons = {
    CheckCircle: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M9 12l2 2 4-4"/></svg>,
    XCircle: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M15 9l-6 6M9 9l6 6"/></svg>,
    AlertTriangle: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
    DollarSign: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>,
    TrendingUp: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>,
    Vault: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><rect x="2" y="2" width="20" height="20" rx="2"/><circle cx="12" cy="12" r="4"/><path d="M12 8v1M12 15v1M8 12h1M15 12h1"/></svg>,
    Swords: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><polyline points="14.5 17.5 3 6 3 3 6 3 17.5 14.5"/><line x1="13" y1="19" x2="19" y2="13"/><line x1="16" y1="16" x2="20" y2="20"/><line x1="19" y1="21" x2="21" y2="19"/><polyline points="14.5 6.5 18 3 21 3 21 6 17.5 9.5"/><line x1="5" y1="14" x2="9" y2="18"/><line x1="7" y1="21" x2="9" y2="19"/></svg>,
    Info: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>,
    Bell: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></svg>,
  };
  return icons?.[name] || icons?.Bell;
};

// ── Single Toast ──────────────────────────────────────────────
const Toast = ({ toast, onDismiss, slideFrom }) => {
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 16);
    return () => clearTimeout(t);
  }, []);

  const handleDismiss = () => {
    setExiting(true);
    setTimeout(() => onDismiss(toast?.id), 300);
  };

  const isCritical = toast?.persistence === TOAST_PERSISTENCE?.CRITICAL;
  const isPersistent = toast?.persistence === TOAST_PERSISTENCE?.PERSISTENT;

  // Slide direction based on position
  const slideTransform = {
    right: visible && !exiting ? 'translateX(0)' : 'translateX(110%)',
    left: visible && !exiting ? 'translateX(0)' : 'translateX(-110%)',
  };
  const transform = slideTransform?.[slideFrom] || slideTransform?.right;

  return (
    <div
      className={`relative flex flex-col rounded-xl border shadow-2xl backdrop-blur-sm max-w-sm w-full pointer-events-auto overflow-hidden ${toast?.colorClass} ${isCritical ? 'ring-1 ring-red-500/40' : ''}`}
      style={{
        transform,
        opacity: visible && !exiting ? 1 : 0,
        transition: 'transform 0.3s cubic-bezier(0.34,1.56,0.64,1), opacity 0.3s ease',
        boxShadow: isCritical ? '0 0 24px rgba(239,68,68,0.3)' : '0 8px 32px rgba(0,0,0,0.5)',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      role="alert"
      aria-live="polite"
    >
      {/* Critical top bar */}
      {isCritical && (
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-red-500 to-orange-500" />
      )}
      <div className="flex items-start gap-3 px-4 pt-3 pb-2">
        <div className="flex-shrink-0 mt-0.5">
          <ToastIcon name={toast?.icon} color={toast?.iconColor} />
        </div>
        <div className="flex-1 min-w-0">
          {toast?.title && (
            <p className="text-xs font-bold uppercase tracking-wider opacity-70 mb-0.5">{toast?.title}</p>
          )}
          <p className="text-sm font-medium leading-snug break-words">{toast?.message}</p>
          {isCritical && (
            <p className="text-xs opacity-50 mt-0.5">Requires acknowledgment</p>
          )}
          {isPersistent && (
            <p className="text-xs opacity-50 mt-0.5">Persistent notification</p>
          )}
        </div>
        <button
          onClick={handleDismiss}
          className="flex-shrink-0 w-5 h-5 rounded flex items-center justify-center opacity-50 hover:opacity-100 transition-opacity"
          aria-label="Dismiss notification"
        >
          <svg width="10" height="10" viewBox="0 0 12 12" fill="currentColor">
            <path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
      </div>
      {/* Action buttons */}
      {toast?.actions && toast?.actions?.length > 0 && (
        <div className="flex gap-2 px-4 pb-3">
          {toast?.actions?.map((action, i) => (
            <button
              key={i}
              onClick={() => { action?.onClick?.(); if (!action?.keepOpen) handleDismiss(); }}
              className={`text-xs px-3 py-1.5 rounded-md font-medium transition-all ${
                action?.primary
                  ? 'bg-white/20 hover:bg-white/30 text-white' : 'bg-white/10 hover:bg-white/15 text-white/80'
              }`}
            >
              {action?.label}
            </button>
          ))}
        </div>
      )}
      {/* Progress bar */}
      {toast?.duration && (
        <ProgressBar
          duration={toast?.duration}
          barColor={toast?.barColor}
          paused={hovered}
        />
      )}
    </div>
  );
};

// ── Mobile Toast with swipe-to-dismiss ────────────────────────────
const MobileToast = ({ toast, onDismiss }) => {
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [swipeX, setSwipeX] = useState(0);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 16);
    return () => clearTimeout(t);
  }, []);

  const handleDismiss = useCallback(() => {
    setExiting(true);
    setTimeout(() => onDismiss(toast?.id), 300);
  }, [toast?.id, onDismiss]);

  const handleTouchStart = (e) => {
    touchStartX.current = e?.touches?.[0]?.clientX;
    touchStartY.current = e?.touches?.[0]?.clientY;
  };

  const handleTouchMove = (e) => {
    if (touchStartX?.current === null) return;
    const dx = e?.touches?.[0]?.clientX - touchStartX?.current;
    const dy = Math.abs(e?.touches?.[0]?.clientY - (touchStartY?.current || 0));
    // Only swipe horizontally if not scrolling vertically
    if (dy < 20) {
      setSwipeX(dx);
    }
  };

  const handleTouchEnd = () => {
    if (Math.abs(swipeX) > 80) {
      // Swiped far enough — dismiss
      setExiting(true);
      setTimeout(() => onDismiss(toast?.id), 300);
    } else {
      // Snap back
      setSwipeX(0);
    }
    touchStartX.current = null;
    touchStartY.current = null;
  };

  const isCritical = toast?.persistence === TOAST_PERSISTENCE?.CRITICAL;
  const swipeOpacity = Math.max(0, 1 - Math.abs(swipeX) / 150);

  return (
    <div
      ref={containerRef}
      className={`relative flex flex-col rounded-xl border shadow-2xl backdrop-blur-sm w-full pointer-events-auto overflow-hidden ${toast?.colorClass} ${isCritical ? 'ring-1 ring-red-500/40' : ''}`}
      style={{
        transform: exiting
          ? `translateX(${swipeX > 0 ? '110%' : '-110%'})`
          : visible
          ? `translateX(${swipeX}px)`
          : 'translateY(100%)',
        opacity: exiting ? 0 : visible ? swipeOpacity : 0,
        transition: swipeX !== 0 ? 'none' : 'transform 0.3s cubic-bezier(0.34,1.56,0.64,1), opacity 0.3s ease',
        willChange: 'transform',
        boxShadow: isCritical ? '0 0 24px rgba(239,68,68,0.3)' : '0 8px 32px rgba(0,0,0,0.5)',
        touchAction: 'pan-y',
      }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      role="alert"
      aria-live="polite"
    >
      {/* Swipe hint indicator */}
      {Math.abs(swipeX) > 20 && (
        <div
          className="absolute inset-y-0 flex items-center px-3 pointer-events-none"
          style={{ [swipeX > 0 ? 'right' : 'left']: 0, opacity: Math.min(Math.abs(swipeX) / 80, 1) }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="2">
            <path d={swipeX > 0 ? 'M9 18l6-6-6-6' : 'M15 18l-6-6 6-6'} />
          </svg>
        </div>
      )}
      {isCritical && (
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-red-500 to-orange-500" />
      )}
      <div className="flex items-start gap-3 px-4 pt-3 pb-2">
        <div className="flex-shrink-0 mt-0.5">
          <ToastIcon name={toast?.icon} color={toast?.iconColor} />
        </div>
        <div className="flex-1 min-w-0">
          {toast?.title && (
            <p className="text-xs font-bold uppercase tracking-wider opacity-70 mb-0.5">{toast?.title}</p>
          )}
          <p className="text-sm font-medium leading-snug break-words">{toast?.message}</p>
        </div>
        <button
          onClick={handleDismiss}
          className="flex-shrink-0 w-6 h-6 rounded flex items-center justify-center opacity-50 active:opacity-100 transition-opacity"
          aria-label="Dismiss notification"
        >
          <svg width="10" height="10" viewBox="0 0 12 12" fill="currentColor">
            <path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
      </div>
      {toast?.actions && toast?.actions?.length > 0 && (
        <div className="flex gap-2 px-4 pb-3">
          {toast?.actions?.map((action, i) => (
            <button
              key={i}
              onClick={() => { action?.onClick?.(); if (!action?.keepOpen) handleDismiss(); }}
              className={`text-xs px-3 py-1.5 rounded-md font-medium transition-all ${
                action?.primary
                  ? 'bg-white/20 active:bg-white/30 text-white' : 'bg-white/10 active:bg-white/15 text-white/80'
              }`}
            >
              {action?.label}
            </button>
          ))}
        </div>
      )}
      {toast?.duration && (
        <ProgressBar
          duration={toast?.duration}
          barColor={toast?.barColor}
          paused={false}
        />
      )}
    </div>
  );
};

// ── ToastStack ────────────────────────────────────────────────
export const ToastStack = ({ toasts, onDismiss, onDismissAll, position = 'bottom-right' }) => {
  if (!toasts || toasts?.length === 0) return null;

  // On mobile, limit visible toasts to 3
  const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)')?.matches;
  const maxVisible = isMobile ? 3 : 5;
  const visibleToasts = toasts?.slice(-maxVisible);
  const queuedCount = Math.max(0, toasts?.length - maxVisible);

  // Position classes
  const positionStyles = {
    'top-right': { top: '1.5rem', right: '1rem', bottom: 'auto', left: 'auto', flexDirection: 'column' },
    'top-left': { top: '1.5rem', left: '1rem', bottom: 'auto', right: 'auto', flexDirection: 'column' },
    'bottom-right': { bottom: '1.5rem', right: '1rem', top: 'auto', left: 'auto', flexDirection: 'column-reverse' },
    'bottom-left': { bottom: '1.5rem', left: '1rem', top: 'auto', right: 'auto', flexDirection: 'column-reverse' },
  };

  const slideFrom = position?.includes('right') ? 'right' : 'left';
  const isBottom = position?.includes('bottom');
  const style = positionStyles?.[position] || positionStyles?.['bottom-right'];

  return (
    <>
      {/* Desktop toast container */}
      <div
        className="fixed z-[9999] flex pointer-events-none hidden sm:flex"
        style={{
          ...style,
          gap: '8px',
          maxWidth: '400px',
          width: 'calc(100vw - 2rem)',
        }}
      >
        {toasts?.length >= 3 && (
          <button
            onClick={onDismissAll}
            className="pointer-events-auto self-end text-xs px-3 py-1.5 rounded-lg backdrop-blur transition-colors"
            style={{
              background: 'rgba(30,30,40,0.9)',
              border: '1px solid rgba(255,255,255,0.1)',
              color: 'rgba(255,255,255,0.6)',
              order: isBottom ? 999 : -1,
            }}
          >
            Dismiss all ({toasts?.length})
          </button>
        )}
        {toasts?.map(toast => (
          <Toast
            key={toast?.id}
            toast={toast}
            onDismiss={onDismiss}
            slideFrom={slideFrom}
          />
        ))}
      </div>

      {/* Mobile toast container — above bottom nav */}
      <div
        className="fixed z-[9999] flex pointer-events-none sm:hidden"
        style={{
          bottom: '80px',
          left: '16px',
          right: '16px',
          top: 'auto',
          flexDirection: 'column-reverse',
          gap: '8px',
          width: 'calc(100vw - 32px)',
          maxWidth: '100%',
        }}
      >
        {/* Queue count badge */}
        {queuedCount > 0 && (
          <div
            className="pointer-events-auto self-center px-3 py-1 rounded-full text-xs font-medium"
            style={{
              background: 'rgba(30,30,40,0.95)',
              border: '1px solid rgba(255,255,255,0.15)',
              color: 'rgba(255,255,255,0.7)',
              zIndex: 1000,
            }}
          >
            +{queuedCount} more
          </div>
        )}
        {visibleToasts?.length >= 2 && (
          <button
            onClick={onDismissAll}
            className="pointer-events-auto self-end text-xs px-3 py-1.5 rounded-lg backdrop-blur transition-colors"
            style={{
              background: 'rgba(30,30,40,0.9)',
              border: '1px solid rgba(255,255,255,0.1)',
              color: 'rgba(255,255,255,0.6)',
            }}
          >
            Dismiss all
          </button>
        )}
        {visibleToasts?.map(toast => (
          <MobileToast
            key={toast?.id}
            toast={toast}
            onDismiss={onDismiss}
          />
        ))}
      </div>
    </>
  );
};

export default ToastProvider;
